import { Amount } from 'src_common/common/amount/Amount';
import { computed, makeAutoObservable } from 'mobx';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { PaymentProvidersType } from 'src/AppState/ConfigState/types';
import { counters } from 'src_server/types';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { AmountPrecision } from 'src_common/common/amount/AmountPrecision';

export type CountersType = TurnoverType | ProfitType;

export type BetsType = 'casino-bets' | 'livecasino-bets' | 'virtualsports-bets' | 'bets';

export type TurnoverType = 'casino-turnover' | 'livecasino-turnover' | 'turnover' | 'virtualsports-turnover';

export type ProfitType =
    | 'livecasino-profit'
    | 'casino-profits'
    | 'operator-profits'
    | 'profits'
    | 'virtualsports-profits';

export type CounterNameType = 'livecasino' | 'casino' | 'virtualsports' | 'all' | 'sports';

export interface TabDataType {
    name: string;
    data: CounterSectionType;
}

export interface CountersNamesType {
    bets: BetsType;
    turnover: TurnoverType;
    profits: ProfitType;
}

export interface CounterType {
    name: string;
    bets: number;
    profit: Amount;
    turnover: Amount;
    avgStake: Amount;
    percent: number;
}

export interface CounterSectionType {
    name: string;
    maxProfit: Amount;
    maxTurnover: Amount;
    counters: CounterType[];
}

export interface AllCountersType {
    sportOverall: CounterSectionType;
    sportBreakdown: CounterSectionType;
    casino: CounterSectionType;
    virtualSports: CounterSectionType;
    liveCasino: CounterSectionType;
}

export class CountersComponentState {
    private readonly countersResource: Resource<counters.ResponseType>;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly amountPrecision: AmountPrecision,
        private readonly customerId: number,
        private readonly paymentProvidersType: PaymentProvidersType
    ) {
        this.countersResource = new Resource(async (): Promise<counters.ResponseType> => {
            return await this.trpcClient.client.counterRouter.getCounters.query({
                customerId: this.customerId,
                paymentProvidersType: this.paymentProvidersType.type,
            });
        });
        makeAutoObservable(this);
    }

    private toPercent(value: number): number {
        return Math.round(value * 10000) / 100;
    }

    @computed public get parsedCounters(): AllCountersType | null {
        if (this.counters.type === 'ready' && this.counters.value.responseStatus === 'success') {
            const all = this.combineCounter('all', this.counters.value.data);
            const casino = this.combineCounter('casino', this.counters.value.data);
            const livecasino = this.combineCounter('livecasino', this.counters.value.data);
            const sports = this.combineCounter('sports', this.counters.value.data);
            const virtualsports = this.combineCounter('virtualsports', this.counters.value.data);

            return {
                sportOverall: all,
                sportBreakdown: sports,
                casino: casino,
                virtualSports: virtualsports,
                liveCasino: livecasino,
            };
        }

        return null;
    }

    public combineCounter(counter: CounterNameType, object: counters.SuccessResponseType): CounterSectionType {
        const names = this.mapCountersName(counter);
        const counters: CounterType[] = [];

        Object.keys(object[names.bets] ?? {}).forEach((item) => {
            if (item !== '*' && counter === 'all') {
                return;
            }

            if ((item === '*' || item === 'sport') && counter === 'sports') {
                return;
            }

            const name = item === '*' ? counter : item.replace('sport/', '');
            const bets = object[names.bets]?.[item];
            const turnover = object[names.turnover]?.[item];
            const profit = object[names.profits]?.[item];

            // - virtualsports_profits returns data from operator's perspective
            // - casino-profits returns data from customer perspective
            // Added minus '-' to everything other than virtualsports_profits.
            const profitMultiply = counter === 'virtualsports' ? 1 : -1;

            if (bets === undefined || turnover === undefined || profit === undefined) {
                return;
            }

            const parsedTurnover = this.amountPrecision.newFromOld(turnover);
            const parsedProfit = this.amountPrecision.newFromOld(profit).multiply(profitMultiply);

            if (bets === 0 || parsedTurnover.isEqualWithZero() || parsedProfit.isEqualWithZero()) {
                return;
            }

            const avgStake = parsedTurnover.div(bets);
            const percent = this.toPercent(parsedProfit.divByAmount(parsedTurnover));

            counters.push({
                bets: bets,
                profit: parsedProfit,
                turnover: parsedTurnover,
                avgStake,
                name,
                percent,
            });
        });

        return {
            counters,
            maxProfit: counters[0]?.profit ?? new Amount('0'),
            maxTurnover: counters[0]?.turnover ?? new Amount('0'),
            name: counter,
        };
    }

    public mapCountersName(counter: CounterNameType): CountersNamesType {
        switch (counter) {
            case 'all':
                return {
                    bets: 'bets',
                    turnover: 'turnover',
                    profits: 'profits',
                };
            case 'sports':
                return {
                    bets: 'bets',
                    turnover: 'turnover',
                    profits: 'profits',
                };
            case 'casino':
                return {
                    bets: 'casino-bets',
                    turnover: 'casino-turnover',
                    profits: 'casino-profits',
                };
            case 'livecasino':
                return {
                    bets: 'livecasino-bets',
                    turnover: 'livecasino-turnover',
                    profits: 'livecasino-profit',
                };
            case 'virtualsports':
                return {
                    bets: 'virtualsports-bets',
                    turnover: 'virtualsports-turnover',
                    profits: 'virtualsports-profits',
                };
        }
    }

    @computed public get counters(): Result<counters.ResponseType> {
        return this.countersResource.get();
    }

    @computed public get allTurnover(): Amount {
        const turnover = this.parsedCounters?.sportOverall.maxTurnover ?? new Amount('0');
        const virtualSportsTurnover = this.parsedCounters?.virtualSports.maxTurnover ?? new Amount('0');
        const liveCasinoTurnover = this.parsedCounters?.liveCasino.maxTurnover ?? new Amount('0');
        const casinoTurnover = this.parsedCounters?.casino.maxTurnover ?? new Amount('0');

        return turnover.add(virtualSportsTurnover).add(liveCasinoTurnover).add(casinoTurnover);
    }

    @computed public get allProfit(): Amount {
        const profits = this.parsedCounters?.sportOverall.maxProfit ?? new Amount('0');
        const virtualSportsProfits = this.parsedCounters?.virtualSports.maxProfit ?? new Amount('0');
        const liveCasinoProfitsr = this.parsedCounters?.liveCasino.maxProfit ?? new Amount('0');
        const casinoProfits = this.parsedCounters?.casino.maxProfit ?? new Amount('0');

        return profits.add(virtualSportsProfits).add(liveCasinoProfitsr).add(casinoProfits).multiply(-1);
    }

    @computed public get lifeTimeTotalPrecentage(): number {
        const finalTurnover = this.allTurnover.isEqualWithZero() === false ? this.allTurnover : new Amount('1');
        return this.toPercent(this.allProfit.divByAmount(finalTurnover)) * -1;
    }

    @computed public get tabs(): TabDataType[] {
        const availableTabs: TabDataType[] = [];
        const counters = this.parsedCounters;

        if (counters === null) {
            return [];
        }

        if (counters.sportOverall.counters.length > 0) {
            availableTabs.push({ name: 'Sport Overall', data: counters.sportOverall });
        }

        if (counters.sportBreakdown.counters.length > 0) {
            availableTabs.push({ name: 'Sport Breakdown', data: counters.sportBreakdown });
        }

        if (counters.casino.counters.length > 0) {
            availableTabs.push({ name: 'Casino', data: counters.casino });
        }

        if (counters.virtualSports.counters.length > 0) {
            availableTabs.push({ name: 'VirtualSports', data: counters.virtualSports });
        }

        if (counters.liveCasino.counters.length > 0) {
            availableTabs.push({ name: 'Livecasino', data: counters.liveCasino });
        }

        return availableTabs;
    }
}
